// Command Center Dispatch Service
// Real API calls for the Command Center dashboard (booking creation, driver
// candidate search, assignment flows, pipeline listing, hospital lookup).
import { api, handleApiError, handleApiSuccess } from './api.js';
import { API_CONFIG } from '../config/api.js';

const { COMMAND_CENTER_ENDPOINTS, ENDPOINTS, HOSPITAL_ENDPOINTS } = API_CONFIG;

export const createBooking = async (payload) => {
  try {
    const response = await api.post(COMMAND_CENTER_ENDPOINTS.CREATE_BOOKING, payload);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Real active EquipmentTypes for the Requirements checklist — replaces what
// used to be a hardcoded frontend list that had to be kept in sync by hand
// with the actual DB names requirementNames matches against.
export const getRequirements = async () => {
  try {
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.REQUIREMENT_OPTIONS);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getSources = async () => {
  try {
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.SOURCE_OPTIONS);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getPatientConditions = async () => {
  try {
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.PATIENT_CONDITION_OPTIONS);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateBooking = async (bookingId, payload) => {
  try {
    const response = await api.patch(`${COMMAND_CENTER_ENDPOINTS.CREATE_BOOKING}/${bookingId}`, payload);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const listBookings = async ({ stage, search, limit, offset, startDate, endDate } = {}) => {
  try {
    const params = { stage, search, limit, offset };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS, { params });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getPipelineCounts = async ({ startDate, endDate } = {}) => {
  try {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.PIPELINE_COUNTS, { params });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getBooking = async (bookingId) => {
  try {
    const response = await api.get(`${COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS}/${bookingId}`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Real GPS route history for a completed trip's map playback — mirrors
// hospitalService.getBookingRoute's shape, backed by Command Center's own
// endpoint (Hospital's is role-gated to HOSPITAL_USER internally).
export const getBookingRoute = async (bookingId) => {
  try {
    const response = await api.get(`${COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS}/${bookingId}/tracking/route`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getBookingTrackingEvents = async (bookingId) => {
  try {
    const response = await api.get(`${COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS}/${bookingId}/tracking/events`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getCandidates = async (bookingId, { maxEtaMinutes, partnerFirst, partnerOnly, search, city, area, pincode, vehicleTypeNames, limit, offset } = {}) => {
  try {
    const response = await api.get(`${COMMAND_CENTER_ENDPOINTS.GET_CANDIDATES}/${bookingId}/candidates`, {
      params: {
        maxEtaMinutes, partnerFirst, partnerOnly, search, city, area, pincode, limit, offset,
        vehicleTypeNames: vehicleTypeNames?.length ? vehicleTypeNames : undefined,
      },
      // axios' default array serialization appends "[]" to the key
      // (vehicleTypeNames[]=...), which the backend's Joi query schema
      // rejects as an unknown field. `indexes: null` forces plain repeated
      // keys (vehicleTypeNames=A&vehicleTypeNames=B) instead.
      paramsSerializer: { indexes: null },
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const searchCandidatesByLocation = async ({
  pickupLatitude,
  pickupLongitude,
  hospitalId,
  maxEtaMinutes,
  partnerFirst,
  partnerOnly,
  search,
  city,
  area,
  pincode,
  vehicleTypeNames,
  limit,
  offset,
} = {}) => {
  try {
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.SEARCH_CANDIDATES || '/command-center-dispatch/candidates/search', {
      params: {
        pickupLatitude,
        pickupLongitude,
        hospitalId: hospitalId || undefined,
        maxEtaMinutes,
        partnerFirst,
        partnerOnly,
        search,
        city: city || undefined,
        area: area || undefined,
        pincode: pincode || undefined,
        limit,
        offset,
        vehicleTypeNames: vehicleTypeNames?.length ? vehicleTypeNames : undefined,
      },
      paramsSerializer: { indexes: null },
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const assignDriver = async (bookingId, { driverId, vehicleId, notifyMethod, callReason, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent }) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.ASSIGN_DRIVER}/${bookingId}/assign`, {
      driverId, vehicleId, notifyMethod, callReason, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const startAutoAssign = async (bookingId, { maxEtaMinutes, vehicleTypeNames, partnerOnly } = {}) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.ASSIGN_DRIVER}/${bookingId}/auto-assign/start`, {
      maxEtaMinutes, vehicleTypeNames, partnerOnly,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const advanceAutoAssign = async (bookingId) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.ASSIGN_DRIVER}/${bookingId}/auto-assign/next`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const cancelPendingRequest = async (bookingId) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.ASSIGN_DRIVER}/${bookingId}/cancel-request`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const assignManualDriver = async (bookingId, { driverName, driverPhone, vehicleType, vehiclePlate, companyName, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent }) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.ASSIGN_MANUAL_DRIVER}/${bookingId}/assign-manual`, {
      driverName, driverPhone, vehicleType, vehiclePlate, companyName, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const reassignDriver = async (bookingId, { driverId, vehicleId, reason, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent }) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.REASSIGN_DRIVER}/${bookingId}/reassign`, {
      driverId, vehicleId, reason, offeredPrice, agreedPrice, partnerPrice, partnerSharePercent,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Cancels the whole booking (distinct from cancelPendingRequest, which only
// cancels a not-yet-answered driver request) — closes it out of the pipeline
// regardless of whether a driver is linked, pending, or already accepted.
export const cancelBooking = async (bookingId, { reason }) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.CANCEL_BOOKING}/${bookingId}/cancel`, { reason });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Soft-deletes a booking (test-data cleanup) — it stops appearing on any
// Trips tab immediately, but the row and its history stay in the DB.
export const deleteBooking = async (bookingId) => {
  try {
    const response = await api.delete(`${COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS}/${bookingId}`);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const estimateFare = async ({ pickupLatitude, pickupLongitude, dropoffLatitude, dropoffLongitude, hospitalId, vehicleTypeName }) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.CREATE_BOOKING}/estimate-fare`, {
      pickupLatitude, pickupLongitude, dropoffLatitude, dropoffLongitude, hospitalId, vehicleTypeName,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const requestHospitalRegistration = async ({ name, address, city, state }) => {
  try {
    const response = await api.post(COMMAND_CENTER_ENDPOINTS.REQUEST_HOSPITAL, { name, address, city, state });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getHospitals = async ({ search, limit = 50 } = {}) => {
  try {
    const response = await api.get(`${ENDPOINTS.HOSPITAL}${HOSPITAL_ENDPOINTS.GET_ALL}`, {
      params: { search, limit },
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Full active AmbulanceTypes master list — GET /ambulances/types has no role
// restriction (optionalAuthMiddleware, no requireRole), unlike the
// TENANT_ADMIN-only /v1/fleet/ambulance-types.
export const getAmbulanceTypes = async () => {
  try {
    const response = await api.get(COMMAND_CENTER_ENDPOINTS.AMBULANCE_TYPES);
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const transitionTripStage = async (bookingId, { targetStage, notes } = {}) => {
  try {
    const response = await api.post(`${COMMAND_CENTER_ENDPOINTS.GET_BOOKINGS}/${bookingId}/transition-stage`, {
      targetStage,
      notes,
    });
    return handleApiSuccess(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

const commandCenterService = {
  createBooking,
  updateBooking,
  getRequirements,
  getSources,
  getPatientConditions,
  estimateFare,
  listBookings,
  getPipelineCounts,
  getBooking,
  deleteBooking,
  getBookingRoute,
  getBookingTrackingEvents,
  getCandidates,
  searchCandidatesByLocation,
  assignDriver,
  startAutoAssign,
  advanceAutoAssign,
  cancelPendingRequest,
  assignManualDriver,
  reassignDriver,
  cancelBooking,
  requestHospitalRegistration,
  getHospitals,
  getAmbulanceTypes,
  transitionTripStage,
};

export default commandCenterService;
